/**
 * FinancialInstallments - Página de Parcelamentos do módulo Financeiro
 * 
 * Layout idêntico à página de Cobranças (FinancialCharges.tsx)
 * Diferença: lista parcelas individuais (financeiro_parcelas) ao invés de cobranças-pai
 * Uma cobrança de 3x aparece como 3 linhas individuais na tabela
 * 
 * @author Sistema
 * @version 2.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação da página
 * RELEASE: 2026-03-17 v2.0.0 - Redesign completo: layout idêntico a Cobranças com parcelas individuais
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseDateLocal } from '@/lib/utils';
import { startOfMonth, endOfMonth, format as formatDateFns } from 'date-fns';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import {
  calculatePeriodDates,
  type PeriodPreset,
} from '@/hooks/useCrmOverviewFilters';
import { FreeTierLimitBanner } from '@/components/financial/FreeTierLimitBanner';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';

const logger = createLogger('FinancialInstallments');

import {
  Receipt,
  CheckCircle2,
  Clock,
  Eye,
  MessageCircle,
  Search,
  X,
  MoreHorizontal,
  SlidersHorizontal,
  CalendarIcon,
  RefreshCw,
  FileText,
  ExternalLink,
  AlertTriangle,
  Crown,
  Package,
  Plus,
} from 'lucide-react';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FinanceiroStatus } from '@/hooks/useFinanceiroCobrancas';
import { useFinanceiroParcelasPaginated, PaginatedParcela, ParcelaPaginatedFilters } from '@/hooks/useFinanceiroParcelasPaginated';
import { useFinanceiroMeiosPagamento, TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { ChargeDetailsModal } from '@/components/financial/ChargeDetailsModal';
import { CreateChargeModal } from '@/components/financial/CreateChargeModal';
import { EditChargeProviderModal } from '@/components/financial/EditChargeProviderModal';
import { Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CategoryFilterSelect } from '@/components/crm/CategoryFilterSelect';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
// v1.0.0 (2026-06-21): resolução com contingência checkout transparente x provider.
import { resolveCheckoutUrl } from '@/lib/financial/resolveCheckoutUrl';

/**
 * Mapa de cores para badge de status
 * Padrão text-[11px] px-2 py-0.5 rounded-full (mesmo de FinancialCharges)
 */
const statusBadgeColors: Record<FinanceiroStatus, string> = {
  aguardando_emissao: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  pendente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  edicao_andamento: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  aguardando_pagamento: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  pago: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  parcial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  vencido: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  cancelado: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  processando_cancelamento: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  falha_cancelamento: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

/** Labels de status para exibição */
const statusLabels: Record<FinanceiroStatus, string> = {
  aguardando_emissao: 'Aguardando emissão',
  pendente: 'Pendente',
  edicao_andamento: 'Edição em andamento',
  aguardando_pagamento: 'Aguardando Pagamento',
  pago: 'Pago',
  parcial: 'Parcial',
  vencido: 'Vencido',
  cancelado: 'Cancelado',
  processando_cancelamento: 'Processando cancelamento',
  falha_cancelamento: 'Falha ao cancelar',
};

/**
 * Cor da barra lateral por status da parcela
 * Segue o padrão visual de FinancialCharges
 */
function getStatusBarColor(status: string): string {
  switch (status) {
    case 'pendente':
      return 'bg-yellow-500';
    case 'edicao_andamento':
      return 'bg-amber-500';
    case 'aguardando_pagamento':
      return 'bg-orange-500';
    case 'pago':
      return 'bg-green-500';
    case 'parcial':
      return 'bg-blue-500';
    case 'vencido':
      return 'bg-red-500';
    case 'cancelado':
      return 'bg-muted-foreground/40';
    default:
      return 'bg-muted-foreground/40';
  }
}

export default function FinancialInstallments() {
  // ====== Estados de filtro ======
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [meioPagamentoFilter, setMeioPagamentoFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  // Debounce da busca para evitar chamadas excessivas à RPC
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Filtro de categoria de produto (botão Mais)
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  // Filtros de data avulsos (botão Mais)
  const [dataVencimentoDe, setDataVencimentoDe] = useState<string | null>(() => formatDateFns(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dataVencimentoAte, setDataVencimentoAte] = useState<string | null>(() => formatDateFns(endOfMonth(new Date()), 'yyyy-MM-dd'));
  // Estado do navegador de mês rápido (header)
  const [monthFilter, setMonthFilter] = useState<Date | null>(new Date());
  // v2.1.0 (2026-07-24) — Preset ativo do MonthNavigator.
  const [activePreset, setActivePreset] = useState<PeriodPreset | null>('month');
  const [customRange, setCustomRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [dataPagamentoDe, setDataPagamentoDe] = useState<string | null>(null);
  const [dataPagamentoAte, setDataPagamentoAte] = useState<string | null>(null);
  // Estado do Sheet lateral "Mais" (sidebar de filtros avançados)
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Estados de paginação (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Estados de modais
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailParcela, setDetailParcela] = useState<PaginatedParcela | null>(null);
  const [createChargeOpen, setCreateChargeOpen] = useState(false);
  // Estado para modal de edição de parcela individual no provider
  const [editProviderOpen, setEditProviderOpen] = useState(false);
  const [editingParcela, setEditingParcela] = useState<PaginatedParcela | null>(null);

  // Hooks
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { meios } = useFinanceiroMeiosPagamento();
  const { hasFinancialModule, isLoadingLimits } = useResourceLimits();
  const navigate = useNavigate();

  // Query: contagem de cobranças ativas para controle de limite (espelho de FinancialCharges)
  const { data: chargeCount = 0 } = useQuery({
    queryKey: ['financeiro-cobrancas-count', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('count_active_financeiro_cobrancas' as any);
      if (error) {
        logger.error('Erro ao contar cobranças ativas', { error });
        return 0;
      }
      return data as number;
    },
    staleTime: 30000,
  });

  /** Limite de 10 cobranças sem add-on modulo_financeiro */
  const isAtLimit = !hasFinancialModule && chargeCount >= 10;

  // Query simples para listar produtos (via RPC segura)
  const { data: products = [] } = useQuery({
    queryKey: ['crm-products-simple', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase.rpc('get_tenant_products_safe', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) return [];
      return (data || []).map((row: { id: string; name: string }) => ({
        id: row.id,
        name: row.name,
      }));
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Debounce da busca textual (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Monta filtros para o hook server-side
  const filters: ParcelaPaginatedFilters = useMemo(() => {
    return {
      status: statusFilter !== 'all' ? statusFilter : null,
      meio_pagamento_id: meioPagamentoFilter !== 'all' ? meioPagamentoFilter : null,
      product_id: productFilter !== 'all' ? productFilter : null,
      category_id: categoryFilter !== 'all' ? categoryFilter : null,
      search: debouncedSearch || null,
      data_vencimento_de: dataVencimentoDe,
      data_vencimento_ate: dataVencimentoAte,
      data_pagamento_de: dataPagamentoDe,
      data_pagamento_ate: dataPagamentoAte,
    };
  }, [statusFilter, meioPagamentoFilter, productFilter, categoryFilter, debouncedSearch, dataVencimentoDe, dataVencimentoAte, dataPagamentoDe, dataPagamentoAte]);

  // Hook de paginação server-side
  const {
    parcelas,
    totalCount,
    kpis,
    isLoading,
    isFetching,
    refetch,
  } = useFinanceiroParcelasPaginated(filters, { page: currentPage, pageSize });

  // Hook de refresh com timestamp (padrão Contratos)
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();

  /** Executa refresh manual dos dados */
  const handleRefresh = useCallback(() => {
    triggerRefresh(async () => {
      refetch();
    });
  }, [triggerRefresh, refetch]);

  /** Handler para mudança de mês no MonthNavigator (setas) */
  const handleMonthChange = useCallback((newDate: Date) => {
    setMonthFilter(newDate);
    setActivePreset('month');
    setCustomRange(null);
    setDataVencimentoDe(formatDateFns(startOfMonth(newDate), 'yyyy-MM-dd'));
    setDataVencimentoAte(formatDateFns(endOfMonth(newDate), 'yyyy-MM-dd'));
  }, []);

  /** Limpar filtro de mês/preset (ver todos) */
  const handleMonthClear = useCallback(() => {
    setMonthFilter(null);
    setActivePreset(null);
    setCustomRange(null);
    setDataVencimentoDe(null);
    setDataVencimentoAte(null);
  }, []);

  /** v2.1.0 — Aplica preset de período (vira range de vencimento). */
  const handlePresetChange = useCallback((preset: PeriodPreset) => {
    setActivePreset(preset);
    if (preset === 'month') {
      const now = new Date();
      setMonthFilter(now);
      setCustomRange(null);
      setDataVencimentoDe(formatDateFns(startOfMonth(now), 'yyyy-MM-dd'));
      setDataVencimentoAte(formatDateFns(endOfMonth(now), 'yyyy-MM-dd'));
      return;
    }
    setCustomRange(null);
    const { startDate, endDate } = calculatePeriodDates(preset);
    setDataVencimentoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataVencimentoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  /** v2.1.0 — Range customizado do MonthNavigator. */
  const handleCustomPeriodChange = useCallback((startDate: Date, endDate: Date) => {
    setActivePreset('custom');
    setCustomRange({ startDate, endDate });
    setDataVencimentoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataVencimentoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  /**
   * Listener Realtime para financeiro_parcelas E financeiro_cobrancas
   * Invalida cache da query paginada em qualquer evento
   */
  useEffect(() => {
    if (!currentTenant?.id) return;

    const channel = supabase
      .channel(`financeiro-parcelas-rt-${currentTenant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financeiro_parcelas',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        (payload) => {
          logger.info('Realtime: parcela alterada', { eventType: payload.eventType });
          queryClient.invalidateQueries({
            queryKey: ['financeiro-parcelas-paginated', currentTenant.id],
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financeiro_cobrancas',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        (payload) => {
          logger.info('Realtime: cobrança alterada (parcelas)', { eventType: payload.eventType });
          queryClient.invalidateQueries({
            queryKey: ['financeiro-parcelas-paginated', currentTenant.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, queryClient]);

  // Reset página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, meioPagamentoFilter, productFilter, categoryFilter, dataVencimentoDe, dataVencimentoAte, dataPagamentoDe, dataPagamentoAte]);

  /** Contador de filtros avançados ativos (para badge do botão Mais) */
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (productFilter !== 'all') count++;
    if (categoryFilter !== 'all') count++;
    if (dataVencimentoDe || dataVencimentoAte) count++;
    if (dataPagamentoDe || dataPagamentoAte) count++;
    return count;
  }, [productFilter, categoryFilter, dataVencimentoDe, dataVencimentoAte, dataPagamentoDe, dataPagamentoAte]);

  /** Verifica se há filtros ativos */
  const hasActiveFilters = search || statusFilter !== 'all' || meioPagamentoFilter !== 'all' || advancedFilterCount > 0;

  /** Limpa todos os filtros */
  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setMeioPagamentoFilter('all');
    setProductFilter('all');
    setCategoryFilter('all');
    setDataVencimentoDe(null);
    setDataVencimentoAte(null);
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
  }, []);

  /** Limpa apenas filtros avançados (dentro do Sheet Mais) */
  const clearAdvancedFilters = useCallback(() => {
    setProductFilter('all');
    setCategoryFilter('all');
    setDataVencimentoDe(null);
    setDataVencimentoAte(null);
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
  }, []);

  /** Formata valor monetário */
  const formatCurrency = (value: number, moeda = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: moeda,
    }).format(value);
  };

  /** Abre modal de detalhes da cobrança-pai ao clicar na linha */
  const handleRowClick = (parcela: PaginatedParcela) => {
    setDetailParcela(parcela);
    setDetailsOpen(true);
  };

  /** Envia link de pagamento via WhatsApp (resolveCheckoutUrl aplica contingência). */
  const handleWhatsApp = (parcela: PaginatedParcela) => {
    const bankSlipUrl = parcela.metadata?.bank_slip_url as string | undefined;
    // Preferência: bank_slip específico da parcela > resolução padrão (mode da conta).
    const resolved = resolveCheckoutUrl({
      checkout_mode: parcela.checkout_mode,
      checkout_session_slug: parcela.checkout_session_slug,
      provider_invoice_url: parcela.provider_invoice_url,
      provider_payment_link: parcela.provider_payment_link,
    });
    const link = bankSlipUrl || resolved.url;
    if (!link) return;
    const valor = formatCurrency(parcela.valor, parcela.moeda);
    const msg = encodeURIComponent(
      `Olá! Segue o link para pagamento da parcela ${parcela.numero_parcela}/${parcela.numero_parcelas} no valor de ${valor}:\n\n${link}\n\nQualquer dúvida, estamos à disposição.`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  /**
   * Monta objeto compatível com ChargeDetailsModal (simula PaginatedCobranca)
   * O modal espera dados da cobrança-pai, que são extraídos da parcela
   */
  const getCobrancaFromParcela = (parcela: PaginatedParcela) => ({
    id: parcela.cobranca_id,
    tenant_id: currentTenant?.id || '',
    customer_id: parcela.customer_id || '',
    deal_id: parcela.deal_id,
    tipo_cobranca: parcela.tipo_cobranca,
    meio_pagamento_id: parcela.meio_pagamento_id,
    conta_corrente_id: parcela.conta_corrente_id,
    descricao: parcela.descricao,
    valor_total: parcela.valor_total,
    moeda: parcela.moeda,
    status: parcela.status, // status da parcela (para referência)
    data_emissao: parcela.parcela_created_at?.substring(0, 10) || null,
    data_vencimento: parcela.data_vencimento,
    observacoes: null,
    numero_parcelas: parcela.numero_parcelas,
    provider_id: null,
    provider_charge_id: parcela.provider_charge_id,
    provider_slug: parcela.provider_slug,
    provider_invoice_url: parcela.provider_invoice_url,
    provider_payment_link: parcela.provider_payment_link,
    created_at: parcela.parcela_created_at,
    updated_at: parcela.parcela_created_at,
    customer_name: parcela.customer_name,
    customer_document: parcela.customer_document,
    tipo_pagamento: parcela.tipo_pagamento,
    conta_corrente_nome: parcela.conta_corrente_nome,
    primeira_data_pagamento: parcela.data_pagamento?.substring(0, 10) || null,
    kpi_total_faturas: 0,
    kpi_total_pagas: 0,
    kpi_recebido: 0,
    kpi_previsto: 0,
    total_count: 0,
  });

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {/* ====== Header Compacto (padrão Cobranças) ====== */}
        {/* Mobile: linha 1 = título + ações | linha 2 = navegador de mês centralizado.
            Desktop (sm+): mantém layout original em 3 colunas (título | mês | ações). */}
        <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
          {/* Bloco título — no mobile abriga as ações alinhadas à direita na mesma linha */}
          <div className="flex items-center justify-between gap-2 sm:block">
            <div>
              <h1 className="text-xl font-semibold">Parcelamentos</h1>
              {/* Subtítulo oculto no mobile */}
              <p className="hidden sm:block text-xs text-muted-foreground">
                Parcelas individuais das cobranças parceladas
              </p>
            </div>
            {/* Ações (mobile) — mesma linha do título, alinhadas à direita */}
            <div className="flex items-center gap-1.5 sm:hidden">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      size="sm"
                      onClick={() => setCreateChargeOpen(true)}
                      disabled={isAtLimit}
                      className="h-8 text-xs gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Nova Cobrança</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                {isAtLimit && (
                  <TooltipContent>
                    Limite de 10 cobranças atingido. Contrate o módulo Financeiro.
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>

          {/* Navegador de mês — centralizado no mobile, no centro do header no desktop */}
          <div className="flex justify-center sm:justify-self-center">
            {monthFilter || (activePreset && activePreset !== 'month') ? (
              <MonthNavigator
                currentMonth={monthFilter ?? new Date()}
                onChange={handleMonthChange}
                onClear={handleMonthClear}
                activePreset={activePreset}
                customRange={customRange}
                onPresetChange={handlePresetChange}
                onCustomChange={handleCustomPeriodChange}
              />
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMonthChange(new Date())}
                className="text-xs text-muted-foreground"
              >
                <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                Filtrar por mês
              </Button>
            )}
          </div>

          {/* Ações (desktop) — alinhadas à direita */}
          <div className="hidden sm:flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              </Button>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    onClick={() => setCreateChargeOpen(true)}
                    disabled={isAtLimit}
                    className="h-8 text-xs gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Nova Cobrança</span>
                  </Button>
                </span>
              </TooltipTrigger>
              {isAtLimit && (
                <TooltipContent>
                  Limite de 10 cobranças atingido. Contrate o módulo Financeiro.
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>


        {/* ====== KPI Cards - Métricas agregadas (padrão horizontal compacto) ====== */}
        {!isLoading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-purple-100 dark:bg-purple-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Receipt className="h-5 w-5 shrink-0 text-purple-700 dark:text-purple-300" />
                    <p className="text-xs font-medium truncate">Total Parcelas</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{totalCount}</p>
                </div>
              </CardContent>
            </Card>

            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-lime-100 dark:bg-green-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-green-700 dark:text-green-300" />
                    <p className="text-xs font-medium truncate">Parcelas Pagas</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">
                    {kpis.totalPagas}/{kpis.totalParcelas}
                    <span className="text-[10px] font-normal text-muted-foreground ml-1">pagas</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-blue-100 dark:bg-blue-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" />
                    <p className="text-xs font-medium truncate">Recebido</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{formatCurrency(kpis.recebido)}</p>
                </div>
              </CardContent>
            </Card>

            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-cyan-100 dark:bg-cyan-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="h-5 w-5 shrink-0 text-cyan-700 dark:text-cyan-300" />
                    <p className="text-xs font-medium truncate">Previsto</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{formatCurrency(kpis.previsto)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ====== Banner — Módulo Financeiro gratuito limitado (componente compartilhado) ====== */}
        <FreeTierLimitBanner entityLabel="parcelas" />


        {/* ====== Barra de Filtros (padrão Cobranças, sem filtro Tipo) ====== */}
        <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
          {/* Busca livre */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
            <Input
              placeholder="Cliente, CPF/CNPJ, Ref. cobrança, N.Fatura..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
            />
          </div>

          {/* Filtro de Status */}
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v)}>
            <SelectTrigger className="w-[85px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro de Meio de Pagamento */}
          <Select value={meioPagamentoFilter} onValueChange={v => setMeioPagamentoFilter(v)}>
            <SelectTrigger className="w-[100px] h-6 text-[11px] sm:w-[160px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Meio Pgto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos meios</SelectItem>
              {meios.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Botão "Mais" — abre Sheet lateral de Filtros Avançados */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMoreFiltersOpen(true)}
            className="h-6 px-2 text-[10px] sm:h-8 sm:px-2.5 sm:text-xs rounded-full gap-1.5"
          >
            <SlidersHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            <span>Mais</span>
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
                {advancedFilterCount}
              </Badge>
            )}
          </Button>

          {/* Sheet lateral — Filtros Avançados */}
          <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
            <SheetContent side="right" className="w-80 p-0 flex flex-col">
              <SheetHeader className="px-4 pt-4 pb-3 border-b">
                <SheetTitle className="flex items-center justify-between text-sm font-semibold">
                  <span>Filtros Avançados</span>
                  {advancedFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAdvancedFilters}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Limpar
                    </Button>
                  )}
                </SheetTitle>
              </SheetHeader>
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-4 space-y-4">
                  {/* Filtro de Produto/Serviço */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Produto/Serviço</Label>
                    <Select value={productFilter} onValueChange={v => setProductFilter(v)}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Produto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos produtos</SelectItem>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Filtro de Categoria */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Categoria</Label>
                    <CategoryFilterSelect
                      value={categoryFilter !== 'all' ? categoryFilter : null}
                      onChange={(id) => setCategoryFilter(id || 'all')}
                      placeholder="Todas categorias"
                      size="xs"
                    />
                  </div>

                  <Separator />

                  {/* Filtro Data Vencimento */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data Vencimento</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataVencimentoDe && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataVencimentoDe ? format(parseDateLocal(dataVencimentoDe), 'dd/MM/yy') : 'De'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataVencimentoDe ? parseDateLocal(dataVencimentoDe) : undefined}
                            onSelect={(d) => setDataVencimentoDe(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataVencimentoAte && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataVencimentoAte ? format(parseDateLocal(dataVencimentoAte), 'dd/MM/yy') : 'Até'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataVencimentoAte ? parseDateLocal(dataVencimentoAte) : undefined}
                            onSelect={(d) => setDataVencimentoAte(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Filtro Data Pagamento */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data Pagamento</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataPagamentoDe && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataPagamentoDe ? format(parseDateLocal(dataPagamentoDe), 'dd/MM/yy') : 'De'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataPagamentoDe ? parseDateLocal(dataPagamentoDe) : undefined}
                            onSelect={(d) => setDataPagamentoDe(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataPagamentoAte && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataPagamentoAte ? format(parseDateLocal(dataPagamentoAte), 'dd/MM/yy') : 'Até'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataPagamentoAte ? parseDateLocal(dataPagamentoAte) : undefined}
                            onSelect={(d) => setDataPagamentoAte(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>

          {/* Botão Limpar Filtros */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}

          {/* Indicador de carregamento */}
          {isFetching && !isLoading && (
            <span className="text-xs text-muted-foreground animate-pulse">
              Atualizando...
            </span>
          )}
        </div>

        {/* ====== Tabela Premium com setinhas de scroll horizontal ====== */}
        <ScrollIndicators className="flex-1">
          <div className="h-full w-full overflow-hidden">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : parcelas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Receipt className="h-12 w-12 text-muted-foreground/40" />
              <div className="text-center">
                <h3 className="font-medium text-sm">Nenhuma parcela encontrada</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {hasActiveFilters
                    ? 'Tente ajustar os filtros de busca'
                    : 'Crie uma cobrança parcelada para ver as parcelas aqui'}
                </p>
              </div>
            </div>
          ) : (
            <Table scrollViewport containerClassName="h-full" className="min-w-[1400px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {/* Barra colorida de status */}
                  <TableHead className="w-2 p-0" />
                  <TableHead className="text-xs font-medium">N.Cob. Pai</TableHead>
                  <TableHead className="text-xs font-medium">Cliente</TableHead>
                  <TableHead className="text-xs font-medium">Descrição</TableHead>
                  <TableHead className="text-xs font-medium">Parcela</TableHead>
                  <TableHead className="text-xs font-medium">N.Fatura</TableHead>
                  <TableHead className="text-xs font-medium">Valor</TableHead>
                  <TableHead className="text-xs font-medium">Status</TableHead>
                  <TableHead className="text-xs font-medium">Vencimento</TableHead>
                  <TableHead className="text-xs font-medium">Dt. Pagamento</TableHead>
                  <TableHead className="text-xs font-medium">Meio Pagamento</TableHead>
                  <TableHead className="text-xs font-medium">Conta Corrente</TableHead>
                  {/* Coluna de ações do provider (boleto PDF, comprovante, link) */}
                  <TableHead className="w-24" />
                  {/* Coluna de ações */}
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcelas.map(parcela => (
                  <TableRow
                    key={parcela.id}
                    className="cursor-pointer hover:bg-muted/40 group"
                    onClick={() => handleRowClick(parcela)}
                  >
                    {/* Barra lateral colorida baseada no status */}
                    <TableCell className="p-0 w-2">
                      <div className={cn(
                        'w-1 h-full min-h-[40px] rounded-full',
                        getStatusBarColor(parcela.status)
                      )} />
                    </TableCell>

                    {/* N.Cob. Pai — 8 primeiros dígitos do UUID da cobrança-pai */}
                    <TableCell className="py-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {parcela.cobranca_id.substring(0, 8).toUpperCase()}
                      </span>
                    </TableCell>

                    {/* Cliente + CPF/CNPJ */}
                    <TableCell className="py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate max-w-[150px]">
                          {parcela.customer_name || 'Sem cliente'}
                        </p>
                        {parcela.customer_document && (
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {formatDocumentForDisplay(parcela.customer_document)}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    {/* Descrição (da cobrança-pai) */}
                    <TableCell className="py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium truncate max-w-[180px]">
                            {parcela.descricao || 'Sem descrição'}
                          </p>
                          {/* Badge "Recorrente" - parcela vinculada a uma assinatura via cobrança-pai */}
                          {parcela.assinatura_id && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-4 bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20 shrink-0"
                              title={
                                parcela.ciclo_numero
                                  ? `Ciclo ${parcela.ciclo_numero} da assinatura`
                                  : 'Parcela gerada por assinatura recorrente'
                              }
                            >
                              Recorrente{parcela.ciclo_numero ? ` #${parcela.ciclo_numero}` : ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>


                    {/* Parcela (ex: "1/3", "2/3") */}
                    <TableCell className="py-2">
                      <Badge variant="outline" className="text-[11px] font-mono">
                        {parcela.numero_parcela}/{parcela.numero_parcelas}
                      </Badge>
                    </TableCell>

                    {/* N.Fatura — número da fatura no provider */}
                    <TableCell className="py-2">
                      <span className="text-xs text-muted-foreground font-mono">
                        {parcela.numero_fatura || '—'}
                      </span>
                    </TableCell>

                    {/* Valor da parcela individual */}
                    <TableCell className="py-2">
                      <span className="text-sm font-mono font-medium">
                        {formatCurrency(parcela.valor, parcela.moeda)}
                      </span>
                    </TableCell>

                    {/* Status da parcela */}
                    <TableCell className="py-2">
                      <span className={cn(
                        'text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                        statusBadgeColors[parcela.status as FinanceiroStatus] || 'bg-gray-100 text-gray-800'
                      )}>
                        {statusLabels[parcela.status as FinanceiroStatus] || parcela.status}
                      </span>
                    </TableCell>

                    {/* Vencimento da parcela */}
                    <TableCell className="py-2">
                      {parcela.data_vencimento ? (
                        <span className="text-xs text-muted-foreground">
                          {format(parseDateLocal(parcela.data_vencimento), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Data Pagamento da parcela */}
                    <TableCell className="py-2">
                      {parcela.data_pagamento ? (
                        <span className="text-xs text-muted-foreground">
                          {format(parseDateLocal(parcela.data_pagamento), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Meio de Pagamento */}
                    <TableCell className="py-2">
                      {parcela.tipo_pagamento ? (
                        <span className="text-xs text-muted-foreground">
                          {TIPOS_PAGAMENTO.find(t => t.value === parcela.tipo_pagamento)?.label || parcela.tipo_pagamento}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Conta Corrente */}
                    <TableCell className="py-2">
                      {parcela.conta_corrente_nome ? (
                        <span className="text-xs text-muted-foreground truncate max-w-[120px] block">
                          {parcela.conta_corrente_nome}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Ações do Provider (Boleto PDF, Comprovante, Link externo) */}
                    <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 flex-nowrap">
                        {/* Boleto PDF — link do metadata da parcela */}
                        {parcela.metadata?.bank_slip_url && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => window.open(parcela.metadata?.bank_slip_url as string, '_blank')}
                              >
                                <FileText className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Abrir Boleto PDF</TooltipContent>
                          </Tooltip>
                        )}

                        {/* Comprovante — link do metadata da parcela */}
                        {parcela.metadata?.transaction_receipt_url && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => window.open(parcela.metadata?.transaction_receipt_url as string, '_blank')}
                              >
                                <Receipt className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Comprovante de Pagamento</TooltipContent>
                          </Tooltip>
                        )}

                        {/* Link de pagamento — usa resolveCheckoutUrl (contingência v1.0.0) */}
                        {(() => {
                          const link = resolveCheckoutUrl({
                            checkout_mode: parcela.checkout_mode,
                            checkout_session_slug: parcela.checkout_session_slug,
                            provider_invoice_url: parcela.provider_invoice_url,
                            provider_payment_link: parcela.provider_payment_link,
                          });
                          if (!link.url) return null;
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => window.open(link.url!, '_blank')}
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {link.usedFallback
                                  ? 'Abrir link (contingência ativada)'
                                  : link.origin === 'transparent'
                                  ? 'Abrir checkout transparente'
                                  : 'Abrir link do provider'}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })()}
                      </div>
                    </TableCell>

                    {/* Ações (DropdownMenu padrão Cobranças) */}
                    <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-7 w-7 transition-opacity',
                              // Parcelas editáveis: ícone sempre visível (descoberta).
                              // Demais: revela apenas no hover (comportamento original).
                              ['aguardando_pagamento', 'vencido'].includes(parcela.status) && parcela.provider_charge_id
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100',
                            )}
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>

                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {/* Ver Detalhes (abre modal da cobrança-pai) */}
                          <DropdownMenuItem onClick={() => handleRowClick(parcela)}>
                            <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
                            Ver Detalhes
                          </DropdownMenuItem>

                          {/* Editar parcela no provider (apenas status editáveis) */}
                          {['aguardando_pagamento', 'vencido'].includes(parcela.status) && parcela.provider_charge_id && (
                            <DropdownMenuItem onClick={() => {
                              setEditingParcela(parcela);
                              setEditProviderOpen(true);
                            }}>
                              <Pencil className="h-4 w-4 mr-2 text-blue-500" />
                              Editar Parcela
                            </DropdownMenuItem>
                          )}

                          {/* WhatsApp */}
                          {(parcela.metadata?.bank_slip_url || parcela.provider_invoice_url || parcela.provider_payment_link) && (
                            <DropdownMenuItem onClick={() => handleWhatsApp(parcela)}>
                              <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
                              Enviar via WhatsApp
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          </div>
        </ScrollIndicators>

        {/* ====== Footer de Paginação (server-side) ====== */}
        {!isLoading && totalCount > 0 && (
          <div className="border-t px-4 py-2 bg-background">
            <PaginationFooter
              totalItems={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              pageSizeOptions={[10, 20, 50, 100]}
              borderClassName=""
              className="mt-0 pt-0"
            />
          </div>
        )}

        {/* Modal de criação de cobrança */}
        <CreateChargeModal
          open={createChargeOpen}
          onOpenChange={setCreateChargeOpen}
        />

        {/* Modal de detalhes da cobrança-pai */}
        <ChargeDetailsModal
          cobranca={detailParcela ? getCobrancaFromParcela(detailParcela) as any : null}
          open={detailsOpen}
          onOpenChange={(v) => {
            setDetailsOpen(v);
            if (!v) setDetailParcela(null);
          }}
        />

        {/* Modal de edição de parcela individual com sync provider */}
        <EditChargeProviderModal
          cobranca={editingParcela ? getCobrancaFromParcela(editingParcela) as any : null}
          parcela={editingParcela ? {
            id: editingParcela.id,
            cobranca_id: editingParcela.cobranca_id,
            tenant_id: currentTenant?.id || '',
            numero_parcela: editingParcela.numero_parcela,
            valor: editingParcela.valor,
            data_vencimento: editingParcela.data_vencimento,
            status: editingParcela.status as any,
            metadata: editingParcela.metadata,
          } as any : null}
          open={editProviderOpen}
          onOpenChange={(v) => {
            setEditProviderOpen(v);
            if (!v) setEditingParcela(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}
